import { Component, OnInit, OnDestroy, NgZone, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { ModalDirective } from "ngx-bootstrap/modal";
import { ActivatedRoute, Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import {
  AnnotationService,
  Annotation,
  Document,
  DocumentService,
} from "./../../../shared/index";
import {
  PDFProgressData,
  PDFDocumentProxy,
  PDFSource as DocumentInitParameters,
} from "ng2-pdf-viewer";

@Component({
  templateUrl: "./annotation-index.html",
  standalone: false,
})
export class AnnotationIndexComponent implements OnInit, OnDestroy {
  @ViewChild("annotationDetails") modal: ModalDirective;
  @ViewChild("errModal") errModal: ModalDirective;

  public annotation: Annotation = new Annotation();
  public document: Document = new Document();
  public annotations: Annotation[] = [];
  public documents: Document[] = [];
  public filtered = false;
  public pdfSrc: string | Uint8Array | DocumentInitParameters | null = null;
  public errModalTitle = "";
  public errModalMsg = "";

  public subscriptions: Subscription[] = [];

  public sanitizedImageUrl: SafeResourceUrl | null = null;

  constructor(
    private _ngZone: NgZone,
    private _annotationService: AnnotationService,
    private _documentService: DocumentService,
    private _route: ActivatedRoute,
    private sanitizer: DomSanitizer // Add DomSanitizer to the constructor
  ) {}

  ngOnInit(): void {
    this._route.data.forEach((data: { annotation: Annotation }) => {
      if (data.annotation) {
        this.annotation = data.annotation;
      }
    });

    this.subscriptions.push(
      this._annotationService.newAnnotation.subscribe((info: Annotation) => {
        this._ngZone.run(() => {
          this.annotations.push(info);
        });
      })
    );

    this.subscriptions.push(
      this._annotationService.annotationDocumentsChange.subscribe(
        (info: Document[]) => {
          this._ngZone.run(() => {
            //this.documents = info;
            // Filter documents to include only one per MetadataId
            const uniqueDocumentsMap = new Map<number, Document>();
            info.forEach((doc) => {
              if (!uniqueDocumentsMap.has(doc.MetadataId)) {
                uniqueDocumentsMap.set(doc.MetadataId, doc);
              }
            });
            this.documents = Array.from(uniqueDocumentsMap.values());
          });
        }
      )
    );

    this.subscriptions.push(
      this._annotationService.annotationsChange.subscribe(
        (info: Annotation[]) => {
          this._ngZone.run(() => {
            this.annotations = info;
          });
        }
      )
    );

    this._annotationService.getAnnotations();
  }

  public filterTo(document: Document): void {
    if (this.document.Id != document.Id) {
      this.document = document;
      this.filtered = true;
    } else {
      this.document = new Document();
      this.filtered = false;
    }
  }

  public updateSanitizedImageUrl(annotationId: number): void {
    const url = `api/Manuals/AnnotationImage?annotationId=${annotationId}`;
    this.sanitizedImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public showDetails(annotation: Annotation): void {
    this.annotation = annotation;
    if (annotation.IsPDF) {
      this.getAnnotationPDF();
    } else {
      this.unloadPDF();
    }

    if (!annotation.IsPDF && annotation.Id) {
      this.updateSanitizedImageUrl(annotation.Id);
    }
    this.modal.show();
  }

  // PDF Viewer Methods
  public onLoadPDF(pdf: PDFDocumentProxy) {
    // do anything with "pdf"
  }

  public onPDFLoadError(error: any) {
    console.error("error loading pdf", error);
  }

  public onPDFLoadProgress(progressData: PDFProgressData) {
    // do anything with progress data. For example progress indicator
  }

  public retrievedPDFId: number = 0;
  public downloadingDocument: boolean = false;
  public isDownloadingPDF: boolean = false;
  public isDownloadingManualPDF: boolean = false;

  public getAnnotationPDF() {
    if (
      this.annotation.Id == 0 ||
      !this.annotation.IsPDF ||
      this.annotation.Id === this.retrievedPDFId
    )
      return;

    this.downloadingDocument = true;
    this._annotationService.downloadPDF(this.annotation.Id).subscribe(
      (data: Blob) => {
        const fileReader = new FileReader();

        fileReader.onloadend = () => {
          this.pdfSrc = new Uint8Array(fileReader.result as ArrayBuffer);
          this.downloadingDocument = false;
        };
        fileReader.readAsArrayBuffer(data);
        this.retrievedPDFId = this.annotation.Id;
      },
      (error: any) => {
        console.error(error);
        this.errModalTitle = "Error Downloading Survey";
        this.errModalMsg = error;
        this.errModal.show();
        this.downloadingDocument = false;
      }
    );
  }

  public unloadPDF() {
    this.pdfSrc = null;
    this.retrievedPDFId = 0;
  }

  public openAnnotationPDF(annotation: Annotation): void {
    if (!annotation.Id || annotation.Id === 0) {
      console.error("Annotation ID not found for opening:", annotation);
      return;
    }

    this.isDownloadingPDF = true;

    this._annotationService.downloadPDF(annotation.Id).subscribe(
      (data: Blob) => {
        this.isDownloadingPDF = false;

        // Create blob URL and open in new tab
        const blobUrl = window.URL.createObjectURL(data);
        window.open(blobUrl, "_blank");

        // Clean up the blob URL after a short delay to allow the browser to load it
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
      },
      (error: any) => {
        console.error("Failed to open PDF:", error);
        this.isDownloadingPDF = false;
        this.errModalTitle = "Open PDF Error";
        this.errModalMsg =
          "Failed to open annotation PDF. Please try again later.";
        this.errModal.show();
      }
    );
  }

  public downloadAnnotationPDF(annotation: Annotation): void {
    if (!annotation.Id || annotation.Id === 0) {
      console.error("Annotation ID not found for download:", annotation);
      return;
    }

    this.isDownloadingPDF = true;

    this._annotationService.downloadPDF(annotation.Id).subscribe(
      (data: Blob) => {
        this.isDownloadingPDF = false;

        // Create a default filename
        let fileName = `annotation-${annotation.Id}.pdf`;

        // Try to get a more descriptive filename if available
        if (annotation.Document && annotation.Document.FileName) {
          fileName = annotation.Document.FileName;
        }

        // Create download link
        const blobUrl = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download =
          fileName +
          "_annotation_" +
          annotation.Submitted.toISOString() +
          ".pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      (error: any) => {
        console.error("Download failed:", error);
        this.isDownloadingPDF = false;
        this.errModalTitle = "Download Error";
        this.errModalMsg =
          "Failed to download annotation PDF. Please try again later.";
        this.errModal.show();
      }
    );
  }

  public openManualPDF(doc: Document): void {
    if (!doc.Id || doc.Id === 0) {
      console.error("Document ID not found for download:", doc);
      return;
    }

    this.isDownloadingManualPDF = true;

    this._documentService.downloadPDF(doc.Id).subscribe(
      (response) => {
        this.isDownloadingManualPDF = false;

        const blob = new Blob([response.body], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        // Open the blob URL in a new tab
        window.open(blobUrl, "_blank");

        // Clean up the blob URL after a short delay to allow the browser to load it
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
      },
      (error: any) => {
        console.error("Download failed:", error);
        this.isDownloadingManualPDF = false;
        this.errModalTitle = "Download Error";
        this.errModalMsg =
          "Failed to download manual PDF. Please try again later.";
        this.errModal.show();
      }
    );
  }

  public downloadManualPDF(doc: Document): void {
    if (!doc.Id || doc.Id === 0) {
      console.error("Document ID not found for download:", doc);
      return;
    }

    this.isDownloadingManualPDF = true;

    this._documentService.downloadPDF(doc.Id).subscribe(
      (response) => {
        this.isDownloadingManualPDF = false;

        const contentDisposition = response.headers.get("Content-Disposition");
        let fileName = `document-${doc.Id}.pdf`; // Default filename

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            fileName = match[1]; // Use extracted filename
          }
        }

        const blob = response.body;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      (error: any) => {
        console.error("Download failed:", error);
        this.isDownloadingManualPDF = false;
        this.errModalTitle = "Download Error";
        this.errModalMsg =
          "Failed to download manual PDF. Please try again later.";
        this.errModal.show();
      }
    );
  }

  // End PDF Viewer Methods

  public closeErrModal(): void {
    this.errModal.hide();
  }

  public close(): void {
    this.modal.hide();
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
}
